import React, { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

interface PendingCarouselGeneration {
  postId: string;
  startedAt: Date;
}

interface CarouselGenerationContextType {
  pendingCarousels: Map<string, PendingCarouselGeneration>;
  startCarouselGeneration: (postId: string, generate: () => Promise<void>) => void;
  isCarouselPending: (postId: string) => boolean;
}

const CarouselGenerationContext = createContext<CarouselGenerationContextType | undefined>(undefined);

export function CarouselGenerationProvider({ children }: { children: ReactNode }) {
  const [pendingCarousels, setPendingCarousels] = useState<Map<string, PendingCarouselGeneration>>(new Map());

  const startCarouselGeneration = useCallback((postId: string, generate: () => Promise<void>) => {
    setPendingCarousels((prev) => {
      const next = new Map(prev);
      next.set(postId, { postId, startedAt: new Date() });
      return next;
    });

    generate().finally(() => {
      setPendingCarousels((prev) => {
        const next = new Map(prev);
        next.delete(postId);
        return next;
      });
    });
  }, []);

  const isCarouselPending = useCallback((postId: string) => pendingCarousels.has(postId), [pendingCarousels]);

  const value = useMemo(
    () => ({ pendingCarousels, startCarouselGeneration, isCarouselPending }),
    [pendingCarousels, startCarouselGeneration, isCarouselPending]
  );

  return (
    <CarouselGenerationContext.Provider value={value}>
      {children}
    </CarouselGenerationContext.Provider>
  );
}

export function useCarouselGenerationContext() {
  const context = useContext(CarouselGenerationContext);
  if (context === undefined) {
    throw new Error('useCarouselGenerationContext must be used within a CarouselGenerationProvider');
  }
  return context;
}